import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { isValidLoginEmail } from '../Auth/loginIdentifier';

const inflightContactStarts = new Map();

function authConfig() {
  const token = localStorage.getItem('token');
  return { headers: { Authorization: `Bearer ${token}` } };
}

function apiErrorMessage(error, fallback) {
  return error?.response?.data?.msg
    || error?.response?.data?.message
    || error?.message
    || fallback;
}

function formatCountdown(totalSeconds) {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

export function normalizeProfilePhone(value) {
  return String(value || '').replace(/\D/g, '').slice(0, 10);
}

export function normalizeProfileEmail(value) {
  return String(value || '').trim().toLowerCase();
}

export function getProfileContactChanges(original, form) {
  const phone = normalizeProfilePhone(form.phone);
  const email = normalizeProfileEmail(form.email);
  const originalPhone = normalizeProfilePhone(original?.phone);
  const originalEmail = normalizeProfileEmail(original?.email);
  if (phone && phone.length !== 10) {
    return { error: 'Enter a valid 10-digit mobile number.' };
  }
  if (email && !isValidLoginEmail(email)) {
    return { error: 'Enter a valid email address.' };
  }
  const changes = [];
  if (phone !== originalPhone) changes.push({ field: 'phone', value: phone });
  if (email !== originalEmail) changes.push({ field: 'email', value: email });
  return { changes };
}

function startContactChange(field, value) {
  const key = `${field}:${value}`;
  const existing = inflightContactStarts.get(key);
  if (existing) return existing;
  const pending = axios.post('/users/profile/contact/start', { field, value }, authConfig())
    .finally(() => inflightContactStarts.delete(key));
  inflightContactStarts.set(key, pending);
  return pending;
}

export function ContactVerifyModal({ field, value, onClose, onVerified }) {
  const isEmail = field === 'email';
  const label = isEmail ? 'email' : 'mobile';
  const [step, setStep] = useState('verifyNew');
  const [sentTo, setSentTo] = useState('');
  const [removing, setRemoving] = useState(!value);
  const [otp, setOtp] = useState('');
  const [status, setStatus] = useState('sending');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageIsError, setMessageIsError] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);

  const busy = status === 'sending' || submitting;

  useEffect(() => {
    if (secondsLeft <= 0) return undefined;
    const timer = setInterval(() => {
      setSecondsLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [secondsLeft]);

  const applyPayload = (payload, infoMessage) => {
    setStep(payload.step || 'verifyNew');
    setSentTo(payload.sentTo || '');
    setRemoving(Boolean(payload.removing));
    const wait = Number(payload.retryAfterSeconds || payload.cooldownSeconds || 0);
    if (wait > 0) setSecondsLeft(Math.ceil(wait));
    setStatus('ready');
    if (infoMessage) {
      setMessageIsError(false);
      setMessage(infoMessage);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setStatus('sending');
      setMessage('');
      setMessageIsError(false);
      setOtp('');
      try {
        const response = await startContactChange(field, value);
        if (cancelled) return;
        const payload = response.data?.data || {};
        applyPayload(
          payload,
          payload.otpStillActive ? 'A code is already active. Enter it below.' : ''
        );
      } catch (error) {
        if (cancelled) return;
        setStatus('error');
        setMessageIsError(true);
        setMessage(apiErrorMessage(error, 'Could not send the code. Please try again.'));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [field, value]);

  const handleResend = async () => {
    if (busy || secondsLeft > 0) return;
    setStatus('sending');
    setMessage('');
    setMessageIsError(false);
    try {
      const response = await axios.post('/users/profile/contact/resend', {}, authConfig());
      applyPayload(response.data?.data || {}, 'A new code has been sent.');
    } catch (error) {
      const retryAfter = Number(error?.response?.data?.data?.retryAfterSeconds);
      if (retryAfter > 0) setSecondsLeft(Math.ceil(retryAfter));
      setStatus('ready');
      setMessageIsError(true);
      setMessage(apiErrorMessage(error, 'Could not resend the code. Please try again.'));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const otpDigits = otp.replace(/\D/g, '');
    if (!/^\d{6}$/.test(otpDigits)) {
      setMessageIsError(true);
      setMessage('Enter the 6-digit code.');
      return;
    }
    setSubmitting(true);
    setMessage('');
    setMessageIsError(false);
    try {
      const response = await axios.post(
        '/users/profile/contact/verify',
        { otp: otpDigits },
        authConfig()
      );
      const payload = response.data?.data || {};
      if (payload.done) {
        onVerified(payload);
        return;
      }
      setOtp('');
      applyPayload(payload, response.data?.msg || 'Code confirmed. Enter the next code.');
    } catch (error) {
      setMessageIsError(true);
      setMessage(apiErrorMessage(error, 'Could not verify the code. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const title = removing
    ? `Remove ${label}`
    : step === 'verifyOld'
      ? `Confirm current ${label}`
      : `Verify new ${label}`;

  const hint = status === 'sending'
    ? `Sending a code to ${sentTo || `your ${label}`}...`
    : status === 'error'
      ? `We could not send a code to your ${label}.`
      : removing
        ? `Enter the code sent to your current ${label} ${sentTo} to remove it.`
        : step === 'verifyOld'
          ? `Enter the code sent to your current ${label} ${sentTo}. We will then send a code to the new one.`
          : `Enter the code sent to your new ${label} ${sentTo}.`;

  return (
    <div className="profile-modal-overlay" onClick={() => !busy && onClose()}>
      <div className="profile-modal" onClick={(event) => event.stopPropagation()} role="dialog" aria-labelledby="contact-verify-title">
        <div className="profile-modal-header">
          <h3 id="contact-verify-title">{title}</h3>
          <button type="button" className="profile-modal-close" onClick={onClose} disabled={busy} aria-label="Close">×</button>
        </div>
        <form onSubmit={handleSubmit} className="profile-modal-body">
          <p className="profile-contact-hint">{hint}</p>
          <div className="profile-modal-field">
            <label htmlFor="contact-verify-otp">Code</label>
            <input
              id="contact-verify-otp"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength="6"
              value={otp}
              placeholder="6-digit code"
              disabled={status !== 'ready' || submitting}
              onChange={(event) => setOtp(event.target.value.replace(/\D/g, '').slice(0, 6))}
            />
          </div>
          <div className="profile-modal-resend">
            {secondsLeft > 0 ? (
              <span>Resend code in {formatCountdown(secondsLeft)}</span>
            ) : (
              <button type="button" onClick={status === 'error' ? undefined : handleResend} disabled={busy || status === 'error'}>
                Resend code
              </button>
            )}
          </div>
          {message && (
            <div className={`profile-modal-message ${messageIsError ? 'profile-modal-message--error' : 'profile-modal-message--success'}`}>
              {message}
            </div>
          )}
          <div className="profile-modal-footer">
            <button type="button" className="profile-modal-btn-secondary" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            {status === 'error' ? (
              <button
                type="button"
                className="profile-modal-btn-primary"
                disabled={busy}
                onClick={() => {
                  inflightContactStarts.delete(`${field}:${value}`);
                  setStatus('sending');
                  startContactChange(field, value)
                    .then((response) => applyPayload(response.data?.data || {}))
                    .catch((error) => {
                      setStatus('error');
                      setMessageIsError(true);
                      setMessage(apiErrorMessage(error, 'Could not send the code. Please try again.'));
                    });
                }}
              >
                Try again
              </button>
            ) : (
              <button type="submit" className="profile-modal-btn-primary" disabled={busy || status !== 'ready'}>
                {submitting ? 'Checking...' : status === 'sending' ? 'Sending...' : (step === 'verifyOld' && !removing ? 'Continue' : 'Confirm')}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
